import { useState, useRef, useEffect } from 'react'
import { Building2, Check, Lock, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useTenantStore, type Tenant } from '@/stores/tenants.ts'

// ─── Chip ────────────────────────────────────────────────────────────────────

interface TenantChipProps {
  tenant: Tenant
  active: boolean
  locked: boolean
  codeOnly?: boolean
  onToggle: () => void
}

function TenantChip({ tenant, active, locked, codeOnly = false, onToggle }: TenantChipProps) {
  const [visible, setVisible] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  function show() { timer.current = setTimeout(() => setVisible(true), 350) }
  function hide() { clearTimeout(timer.current); setVisible(false) }

  const hint = locked
    ? 'letzter aktiver Mandant – kann nicht deaktiviert werden'
    : active
    ? 'klicken zum Deaktivieren'
    : 'klicken zum Aktivieren'

  return (
    <div
      role="checkbox"
      aria-checked={active}
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={locked ? undefined : onToggle}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !locked) {
          e.preventDefault()
          onToggle()
        }
      }}
      className={cn(
        'relative inline-flex items-center gap-1 h-5 rounded-full border select-none whitespace-nowrap flex-shrink-0',
        'py-px pl-[5px] pr-[7px] text-[10.5px] leading-none',
        'transition-[background-color,color,border-color] duration-[120ms]',
        active
          ? [
              'text-white bg-brand-600/50 border-brand-600/50',
              '[box-shadow:0_1px_0_rgba(15,44,77,0.04)]',
              locked
                ? 'cursor-default [box-shadow:inset_0_0_0_1.5px_rgba(255,255,255,0.18)]'
                : 'cursor-pointer hover:bg-brand-600/65 hover:border-brand-600/65',
            ]
          : 'bg-white text-gray-500 border-gray-300 cursor-pointer hover:bg-gray-100 hover:border-gray-400 hover:text-gray-700',
      )}
    >
      {active && (
        <span className="inline-grid place-items-center w-[11px] h-[11px] rounded-full bg-white/20 flex-shrink-0">
          {locked
            ? <Lock size={8} strokeWidth={2.5} />
            : <Check size={9} strokeWidth={3} />
          }
        </span>
      )}
      <span className="font-bold text-[10px] tracking-[0.02em]">{tenant.code}</span>
      {!codeOnly && (
        <span className={cn('text-[10.5px]', active ? 'opacity-85' : 'opacity-65')}>
          {tenant.name}
        </span>
      )}

      {visible && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+8px)] z-50 pointer-events-none whitespace-nowrap bg-gray-700 text-white px-[10px] py-[6px] rounded-md text-[11.5px] font-medium leading-snug [box-shadow:0_6px_18px_rgba(0,0,0,0.18)]">
          {tenant.name}
          <span className="block text-[10.5px] font-normal opacity-70 mt-px">{hint}</span>
          <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-700" />
        </span>
      )}
    </div>
  )
}

// ─── Dropdown (mobile < md) ───────────────────────────────────────────────────

interface MobileDropdownProps {
  tenants: Tenant[]
  activeTenantIds: string[]
  onToggle: (id: string) => void
  onAll: () => void
  onNone: () => void
}

function MobileDropdown({ tenants, activeTenantIds, onToggle, onAll, onNone }: MobileDropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const activeCount = activeTenantIds.length

  useEffect(() => {
    if (!open) return
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  const visibleCodes = tenants.filter((t) => activeTenantIds.includes(t.id)).slice(0, 3)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 h-7 px-[10px] border border-gray-300 rounded-md bg-white text-gray-700 text-xs font-medium hover:border-brand-500 transition-colors"
      >
        <span className="inline-flex">
          {visibleCodes.map((t, i) => (
            <span
              key={t.id}
              style={{ marginLeft: i > 0 ? -6 : 0 }}
              className="inline-grid place-items-center w-[18px] h-[18px] rounded bg-brand-600 text-white text-[9px] font-bold border-[1.5px] border-white"
            >
              {t.code.slice(0, 3)}
            </span>
          ))}
        </span>
        <span className="font-semibold">{activeCount} aktiv</span>
        <span className="text-gray-400">/ {tenants.length}</span>
        {open ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-lg z-50 [box-shadow:0_8px_24px_rgba(58,15,94,0.18),0_0_0_1px_rgba(58,15,94,0.06)] p-1.5">
          <div className="text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider px-2 pt-1.5 pb-1">
            Mandanten
          </div>
          {tenants.map((t) => {
            const active = activeTenantIds.includes(t.id)
            const locked = active && activeTenantIds.length === 1
            return (
              <div
                key={t.id}
                onClick={locked ? undefined : () => { onToggle(t.id); }}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-50 text-sm text-gray-700"
              >
                <span className={cn(
                  'inline-grid place-items-center w-4 h-4 rounded flex-shrink-0 border-[1.5px]',
                  active
                    ? 'bg-brand-600 border-brand-600 text-white'
                    : 'border-gray-300',
                )}>
                  {active && <Check size={10} strokeWidth={3} />}
                </span>
                <span className="font-bold text-[11.5px] min-w-[32px]">{t.code}</span>
                <span className="text-xs text-gray-500 flex-1">{t.name}</span>
              </div>
            )
          })}
          <div className="h-px bg-gray-100 my-1" />
          <div className="flex gap-1 px-1 pb-1">
            <button
              onClick={() => { onAll(); setOpen(false) }}
              className="flex-1 text-left text-[11.5px] font-medium text-gray-500 px-2 py-1 rounded hover:bg-gray-100 hover:text-brand-600 transition-colors"
            >
              Alle auswählen
            </button>
            <button
              onClick={() => { onNone(); setOpen(false) }}
              className="flex-1 text-right text-[11.5px] font-medium text-gray-500 px-2 py-1 rounded hover:bg-gray-100 hover:text-brand-600 transition-colors"
            >
              Keine
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Context Bar ──────────────────────────────────────────────────────────────

export function TenantContextBar() {
  const tenants = useTenantStore((s) => s.tenants)
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const toggleTenant = useTenantStore((s) => s.toggleTenant)
  const setActiveTenantIds = useTenantStore((s) => s.setActiveTenantIds)
  const fetchTenants = useTenantStore((s) => s.loaded)
  const load = useTenantStore((s) => s.fetchTenants)

  useEffect(() => {
    if (!fetchTenants) void load()
  }, [fetchTenants, load])

  if (tenants.length < 2) return null

  const activeCount = activeTenantIds.length
  const allActive = activeCount === tenants.length

  function handleAll() { setActiveTenantIds(tenants.map((t) => t.id)) }
  function handleNone() {
    const first = tenants[0]
    if (first) setActiveTenantIds([first.id])
  }

  return (
    <div
      className="flex-shrink-0 bg-gray-50 border-b border-gray-200 flex items-center gap-[10px] px-5 min-h-[32px]"
      role="group"
      aria-label="Mandantenfilter"
    >
      {/* Label */}
      <span className="inline-flex items-center gap-[5px] text-[10.5px] text-gray-500 font-medium flex-shrink-0">
        <Building2 size={12} className="text-gray-400" />
        <span className="hidden sm:inline">Mandant:</span>
      </span>

      {/* Divider */}
      <span className="w-px h-[14px] bg-gray-200 flex-shrink-0" />

      {/* Chips: xl+ = code+name, md-xl = code only + scroll, <md = dropdown */}
      <div className="flex-1 min-w-0 overflow-hidden flex items-center">

        {/* Mobile dropdown (<md) */}
        <div className="md:hidden w-full">
          <MobileDropdown
            tenants={tenants}
            activeTenantIds={activeTenantIds}
            onToggle={toggleTenant}
            onAll={handleAll}
            onNone={handleNone}
          />
        </div>

        {/* md-xl: scroll, code-only chips */}
        <div className="hidden md:flex xl:hidden items-center gap-[6px] overflow-x-auto scrollbar-none py-[2px] flex-1 relative">
          {tenants.map((t) => {
            const active = activeTenantIds.includes(t.id)
            const locked = active && activeTenantIds.length === 1
            return (
              <TenantChip
                key={t.id}
                tenant={t}
                active={active}
                locked={locked}
                codeOnly
                onToggle={() => toggleTenant(t.id)}
              />
            )
          })}
          {/* fade-right fuer scroll-indikator */}
          <div className="absolute right-0 top-0 h-full w-9 pointer-events-none bg-gradient-to-r from-transparent to-gray-50 flex-shrink-0" />
        </div>

        {/* xl+: full chips with name + quick actions */}
        <div className="hidden xl:flex items-center gap-[6px] flex-1 min-w-0 overflow-x-auto scrollbar-none py-[2px]">
          {tenants.map((t) => {
            const active = activeTenantIds.includes(t.id)
            const locked = active && activeTenantIds.length === 1
            return (
              <TenantChip
                key={t.id}
                tenant={t}
                active={active}
                locked={locked}
                onToggle={() => toggleTenant(t.id)}
              />
            )
          })}
        </div>
      </div>

      {/* Quick actions (xl+ only) */}
      <div className="hidden xl:inline-flex items-center gap-1 flex-shrink-0 ml-auto pl-2">
        <span className="text-[10.5px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-[7px] py-[2px] rounded-full">
          {activeCount}/{tenants.length}
        </span>
        <button
          disabled={allActive}
          onClick={handleAll}
          className="text-[11.5px] font-medium text-gray-500 px-2 py-1 rounded hover:bg-gray-100 hover:text-brand-600 transition-colors disabled:text-gray-400 disabled:pointer-events-none"
        >
          Alle
        </button>
        <span className="w-1 h-1 rounded-full bg-gray-300" />
        <button
          onClick={handleNone}
          className="text-[11.5px] font-medium text-gray-500 px-2 py-1 rounded hover:bg-gray-100 hover:text-brand-600 transition-colors"
        >
          Keiner
        </button>
      </div>
    </div>
  )
}
